import { useState, useCallback } from 'react';

interface ElevenLabsOptions {
  stability?: number;
  similarityBoost?: number;
  style?: number;
  useSpeakerBoost?: boolean;
}

interface UseElevenLabsProps {
  apiKey?: string;
  voiceId?: string;
}

export const useElevenLabs = ({ apiKey, voiceId }: UseElevenLabsProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [lastText, setLastText] = useState<string>('');
  const [lastOptions, setLastOptions] = useState<ElevenLabsOptions>({});
  const [isBrowserTTS, setIsBrowserTTS] = useState(false);

  const stop = useCallback(() => {
    console.log('🛑 Stop called, currentAudio:', currentAudio);
    setCurrentAudio(prevAudio => {
      if (prevAudio) {
        console.log('🛑 Stopping audio');
        prevAudio.pause();
        prevAudio.currentTime = 0;
        URL.revokeObjectURL(prevAudio.src);
        setIsPlaying(false);
      }
      return null;
    });
  }, [currentAudio]);

  const toggleAudio = useCallback(() => {
    console.log('🔄 Toggle called, isPlaying:', isPlaying, 'currentAudio:', currentAudio);
    
    // If we don't have API credentials, use browser TTS toggle
    if (!apiKey || !voiceId) {
      console.log('🔄 Using browser TTS toggle');
      if (isBrowserTTS) {
        console.log('⏸️ Stopping browser TTS');
        window.speechSynthesis.cancel();
        setIsBrowserTTS(false);
        setIsPlaying(false);
      } else if (lastText) {
        console.log('▶️ Starting browser TTS');
        const utterance = new SpeechSynthesisUtterance(lastText);
        utterance.rate = 0.8;
        utterance.pitch = 1;
        utterance.volume = 1;
        utterance.onstart = () => {
          setIsBrowserTTS(true);
          setIsPlaying(true);
        };
        utterance.onend = () => {
          setIsBrowserTTS(false);
          setIsPlaying(false);
        };
        window.speechSynthesis.speak(utterance);
      }
      return;
    }
    
    if (currentAudio && !currentAudio.paused) {
      // Audio is playing, pause it
      console.log('⏸️ Pausing audio');
      currentAudio.pause();
      setIsPlaying(false);
    } else if (currentAudio && currentAudio.paused) {
      // Audio is paused, try to resume
      console.log('▶️ Resuming audio');
      currentAudio.play().catch(() => {
        console.log('❌ Resume failed, restarting from beginning');
        currentAudio.currentTime = 0;
        currentAudio.play().catch(() => {
          console.log('❌ Restart failed, regenerating audio');
          if (lastText) {
            speak(lastText, lastOptions);
          }
        });
      });
      setIsPlaying(true);
    } else {
      // No audio or audio ended, replay from last text
      console.log('🔄 No current audio, replaying last text:', lastText);
      if (lastText) {
        speak(lastText, lastOptions);
      }
    }
  }, [currentAudio, isPlaying, lastText, lastOptions]);

  const speak = useCallback(async (text: string, options: ElevenLabsOptions = {}) => {
    console.log('🗣️ Speak called with text:', text.substring(0, 50) + '...');
    
    if (!apiKey || !voiceId) {
      console.error('❌ Missing API credentials:', { apiKey: !!apiKey, voiceId: !!voiceId });
      throw new Error('ElevenLabs API key and voice ID are required');
    }

    try {
      setIsLoading(true);
      setError(null);
      setLastText(text);
      setLastOptions(options);
      setLastText(text);
      setLastOptions(options);

      // Stop any currently playing audio
      stop();

      // Add timeout for ElevenLabs API call
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${voiceId}`, {
        method: 'POST',
        headers: {
          'Accept': 'audio/mpeg',
          'Content-Type': 'application/json',
          'xi-api-key': apiKey,
        },
        body: JSON.stringify({
          text,
          model_id: 'eleven_monolingual_v1',
          voice_settings: {
            stability: options.stability || 0.5,
            similarity_boost: options.similarityBoost || 0.8,
            style: options.style || 0.2,
            use_speaker_boost: options.useSpeakerBoost || true,
          },
        }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`ElevenLabs API error: ${response.status}`);
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioUrl);

      audio.onplay = () => {
        console.log('🎵 Audio started playing');
        setIsPlaying(true);
      };
      
      audio.onpause = () => {
        console.log('⏸️ Audio paused');
        setIsPlaying(false);
      };
      
      audio.onended = () => {
        console.log('🏁 Audio ended');
        setIsPlaying(false);
        URL.revokeObjectURL(audioUrl);
        setCurrentAudio(null);
      };
      
      audio.onerror = () => {
        console.log('❌ Audio error');
        setError('Failed to play audio');
        setIsPlaying(false);
        setCurrentAudio(null);
      };

      setCurrentAudio(audio);
      await audio.play();
      console.log('✅ Audio setup complete and playing');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('❌ Speak error:', errorMessage);
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [apiKey, voiceId, stop, lastText, lastOptions]);

  return {
    speak,
    stop,
    toggleAudio,
    isPlaying,
    isLoading,
    error,
  };
};